import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { Star, StarFill } from "react-bootstrap-icons";
import { useAuth } from "../../Context/AuthContext";
import "./ResolutionFeedbackModal.css";

const ResolutionFeedbackModal = () => {
  const { user } = useAuth();
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  const [complaint, setComplaint] = useState(null);
  const [rating, setRating] = useState(0);
  const [suggestion, setSuggestion] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const loadPendingFeedback = async () => {
    if (!user?.email) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${baseUrl}/user-api/resolution-feedback/pending`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setComplaint(response.data.complaint);
    } catch (requestError) {
      console.error("Unable to load resolution feedback:", requestError);
      setComplaint(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.email) {
      setComplaint(null);
      setLoading(false);
      return;
    }

    loadPendingFeedback();
  }, [user?.email]);

  const respond = async (action) => {
    if (!complaint || (action === "submitted" && !rating)) {
      if (!rating) {
        setError("Please select a star rating before submitting.");
      }
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${baseUrl}/user-api/resolution-feedback/${complaint.complaint_id}`,
        {
          action,
          rating,
          suggestion,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setRating(0);
      setSuggestion("");

      if (action === "submitted") {
        setComplaint(null);
        setShowSuccess(true);
      } else {
        setComplaint(null);
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "Unable to save your feedback. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <>
      {complaint && (
        <Modal
          show
          centered
          backdrop="static"
          keyboard={false}
          contentClassName="resolution-feedback-modal"
        >
          <Modal.Body className="p-4 text-center">
            <div className="feedback-icon" aria-hidden="true">
              ★
            </div>

            <h4 className="mb-2">How was your resolution?</h4>

            <p className="text-muted mb-1">
              Your request, <strong>{complaint.title}</strong>, was resolved.
            </p>

            <p className="small text-muted">
              Your feedback helps us improve.
            </p>

            <div
              className="star-rating"
              role="radiogroup"
              aria-label="Rate your resolution from one to five stars"
            >
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="star-button"
                  onClick={() => {
                    setRating(star);
                    setError("");
                  }}
                  aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  aria-pressed={rating === star}
                  disabled={submitting}
                >
                  {star <= rating ? <StarFill /> : <Star />}
                </button>
              ))}
            </div>

            <Form.Group className="text-start mt-3">
              <Form.Label className="small">
                Suggestions <span className="text-muted">(optional)</span>
              </Form.Label>

              <Form.Control
                as="textarea"
                rows={3}
                maxLength={1000}
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                placeholder="Tell us how we could improve..."
                disabled={submitting}
              />
            </Form.Group>

            {error && (
              <div className="text-danger small mt-2">{error}</div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-4 gap-3">
              <Button
                variant="link"
                className="text-muted p-0"
                onClick={() => respond("skipped")}
                disabled={submitting}
              >
               Not now
              </Button>

              <Button
                variant="primary"
                onClick={() => respond("submitted")}
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Submit feedback"
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <Modal
        show={showSuccess}
        centered
        onHide={() => setShowSuccess(false)}
      >
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: "3rem" }}> 🎉</div>

          <h4 className="mt-3">Thank You!</h4>

          <p className="text-muted">
            Your feedback has been submitted successfully.
            <br />
            We appreciate your valuable feedback.
          </p>

          <Button
            variant="primary"
            onClick={() => setShowSuccess(false)}
          >
            OK
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ResolutionFeedbackModal;